import { useEffect, useState } from "react";
import { useLang } from "../contexts/LangContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, X, Check, DollarSign, TrendingUp, Eye, Ban, Trash2, Plus, Pencil, Users, Coins as CoinsIcon, ArrowDownCircle, ArrowUpCircle, Megaphone, Send } from "lucide-react";

export default function Admin() {
  const { t } = useLang();
  const [tab, setTab] = useState("deposits");
  const [pending, setPending] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [users, setUsers] = useState([]);
  const [revenue, setRevenue] = useState({ grand_total: 0, by_event: [], recent: [], total_deposits_amount: 0, total_deposits_count: 0, total_ad_views: 0, total_users: 0, total_blocked: 0 });
  const [pools, setPools] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [promos, setPromos] = useState([]);
  const [revWallet, setRevWallet] = useState({ total_revenue: 0, withdrawn: 0, available: 0, by_category: [], history: [] });

  // Dialog states
  const [previewDoc, setPreviewDoc] = useState(null);
  const [rejectKyc, setRejectKyc] = useState(null);
  const [rejectWd, setRejectWd] = useState(null);
  const [rejectDep, setRejectDep] = useState(null);
  const [rejectPromo, setRejectPromo] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [poolForm, setPoolForm] = useState(null);
  const [adjustUser, setAdjustUser] = useState(null); // {user_id, name, amount, reason}
  const [revWithdraw, setRevWithdraw] = useState(null); // {amount, method, destination}
  const [notifyForm, setNotifyForm] = useState(null); // {title, body, target_user_id?}

  const load = async () => {
    const [p, w, u, r, pl, dep, pr, rw] = await Promise.all([
      api.get("/admin/kyc/pending"),
      api.get("/admin/withdrawals"),
      api.get("/admin/users"),
      api.get("/admin/revenue"),
      api.get("/pools"),
      api.get("/admin/deposits"),
      api.get("/admin/promos"),
      api.get("/admin/revenue/wallet"),
    ]);
    setPending(p.data); setWithdrawals(w.data); setUsers(u.data); setRevenue(r.data); setPools(pl.data);
    setDeposits(dep.data); setPromos(pr.data); setRevWallet(rw.data);
  };
  useEffect(() => { load(); }, []);

  // KYC actions
  const previewKycDoc = async (kyc_id) => {
    try { const { data } = await api.get(`/admin/kyc/${kyc_id}/document`);
      setPreviewDoc(data);
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const approveKyc = async (kyc_id) => {
    try { await api.post(`/admin/kyc/${kyc_id}/action`, { action: "approve" });
      toast.success("✓ تم القبول"); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const submitRejectKyc = async () => {
    if (!rejectKyc?.note?.trim()) { toast.error("اكتب سبب الرفض"); return; }
    try { await api.post(`/admin/kyc/${rejectKyc.kyc_id}/action`, { action: "reject", note: rejectKyc.note });
      toast.success("تم الرفض"); setRejectKyc(null); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // Withdrawal actions
  const approveWd = async (wd_id) => {
    try { await api.post(`/admin/withdrawals/${wd_id}/action`, { action: "approve" });
      toast.success("✓ تم القبول"); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const submitRejectWd = async () => {
    if (!rejectWd?.note?.trim()) { toast.error("اكتب سبب الرفض"); return; }
    try { await api.post(`/admin/withdrawals/${rejectWd.wd_id}/action`, { action: "reject", note: rejectWd.note });
      toast.success("تم الرفض"); setRejectWd(null); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // Deposit actions
  const approveDep = async (id) => {
    try { await api.post(`/admin/deposits/${id}/action`, { action: "approve" });
      toast.success("✓ تم القبول وأُضيف الرصيد"); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const submitRejectDep = async () => {
    if (!rejectDep?.note?.trim()) { toast.error("اكتب سبب الرفض"); return; }
    try { await api.post(`/admin/deposits/${rejectDep.dep_id}/action`, { action: "reject", note: rejectDep.note });
      toast.success("تم الرفض"); setRejectDep(null); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // Balance adjust
  const submitAdjust = async () => {
    if (!adjustUser?.reason?.trim()) { toast.error("اكتب السبب"); return; }
    if (!adjustUser?.amount) { toast.error("أدخل المبلغ"); return; }
    try { await api.post(`/admin/users/${adjustUser.user_id}/adjust-balance`,
        { amount: Number(adjustUser.amount), reason: adjustUser.reason });
      toast.success("✓ تم التعديل"); setAdjustUser(null); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // Revenue withdrawal
  const submitRevWithdraw = async () => {
    if (!revWithdraw?.amount || !revWithdraw?.destination) { toast.error("أدخل البيانات"); return; }
    try { await api.post("/admin/revenue/withdraw", {
        amount: Number(revWithdraw.amount), method: revWithdraw.method || "trx",
        destination: revWithdraw.destination });
      toast.success("✓ تم تقديم طلب السحب"); setRevWithdraw(null); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // Promo actions
  const approvePromo = async (id) => {
    try { await api.post(`/admin/promos/${id}/action`, { action: "approve" });
      toast.success("✓ تم القبول"); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const submitRejectPromo = async () => {
    if (!rejectPromo?.note?.trim()) { toast.error("اكتب سبب الرفض"); return; }
    try { await api.post(`/admin/promos/${rejectPromo.promo_id}/action`, { action: "reject", note: rejectPromo.note });
      toast.success("تم الرفض"); setRejectPromo(null); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // Send notification
  const submitNotify = async () => {
    if (!notifyForm?.title?.trim() || !notifyForm?.body?.trim()) { toast.error("اكتب العنوان والنص"); return; }
    try { await api.post("/admin/notify", notifyForm);
      toast.success("✓ تم الإرسال"); setNotifyForm(null);
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // Admin KYC bypass (skip / reset)
  const kycSkip = async (uid) => {
    try { await api.post(`/admin/users/${uid}/kyc-skip`);
      toast.success("✓ تم توثيق الحساب يدوياً"); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const kycReset = async (uid) => {
    if (!window.confirm("إعادة تعيين توثيق هذا المستخدم؟")) return;
    try { await api.post(`/admin/users/${uid}/kyc-reset`);
      toast.success("تم الإعادة"); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  const [search, setSearch] = useState({ query: "", results: [], detail: null });

  // Master Transaction Log
  const [ledger, setLedger] = useState({ query: "", type: "", rows: [] });
  const loadLedger = async () => {
    try {
      const params = new URLSearchParams();
      if (ledger.query) params.set("q", ledger.query);
      if (ledger.type) params.set("type", ledger.type);
      const { data } = await api.get(`/admin/transactions?${params.toString()}`);
      setLedger((s) => ({ ...s, rows: data }));
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  useEffect(() => { if (tab === "ledger") loadLedger(); /* eslint-disable-next-line */ }, [tab]);

  // Referrals stats
  const [refs, setRefs] = useState([]);
  const loadRefs = async () => {
    try { const { data } = await api.get("/admin/referrals"); setRefs(data); }
    catch (err) { toast.error(err.response?.data?.detail); }
  };
  useEffect(() => { if (tab === "referrals") loadRefs(); /* eslint-disable-next-line */ }, [tab]);

  // Fallback videos (admin-managed)
  const [fallbacks, setFallbacks] = useState([]);
  const [fbForm, setFbForm] = useState({ url: "", platform: "youtube", label: "" });
  const loadFallbacks = async () => {
    try { const { data } = await api.get("/admin/fallback-videos"); setFallbacks(data); }
    catch (err) { toast.error(err.response?.data?.detail); }
  };
  useEffect(() => { if (tab === "fallback") loadFallbacks(); /* eslint-disable-next-line */ }, [tab]);
  const addFallback = async () => {
    if (!fbForm.url.trim()) { toast.error("اكتب رابط الفيديو"); return; }
    try {
      await api.post("/admin/fallback-videos", fbForm);
      toast.success("✓ تمت الإضافة");
      setFbForm({ url: "", platform: "youtube", label: "" });
      loadFallbacks();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const toggleFallback = async (fv_id) => {
    try { await api.patch(`/admin/fallback-videos/${fv_id}`); loadFallbacks(); }
    catch (err) { toast.error(err.response?.data?.detail); }
  };
  const deleteFallback = async (fv_id) => {
    if (!window.confirm("حذف هذا الفيديو الاحتياطي؟")) return;
    try { await api.delete(`/admin/fallback-videos/${fv_id}`); loadFallbacks(); toast.success("تم الحذف"); }
    catch (err) { toast.error(err.response?.data?.detail); }
  };

  const doSearch = async () => {
    try {
      const { data } = await api.get(`/admin/users/search?q=${encodeURIComponent(search.query || "")}`);
      setSearch((s) => ({ ...s, results: data }));
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const openUserDetail = async (uid) => {
    try {
      const { data } = await api.get(`/admin/users/${uid}/profile`);
      setSearch((s) => ({ ...s, detail: data }));
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const verifyRevenue = async () => {
    try {
      const { data } = await api.post("/admin/revenue/verify");
      if (data.match) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // User actions
  const toggleBlock = async (u) => {
    try { await api.post(`/admin/users/${u.user_id}/action`, { action: u.is_blocked ? "unblock" : "block" });
      toast.success(u.is_blocked ? "تم رفع الحظر" : "تم الحظر"); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const submitDeleteUser = async () => {
    try { await api.post(`/admin/users/${confirmDeleteUser.user_id}/action`, { action: "delete" });
      toast.success("تم الحذف"); setConfirmDeleteUser(null); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  // Pool actions
  const submitPool = async () => {
    try {
      if (poolForm.pool_id) {
        await api.patch(`/admin/pools/${poolForm.pool_id}`, {
          name: poolForm.name, description: poolForm.description,
          amount_per_round: Number(poolForm.amount_per_round), member_count: Number(poolForm.member_count),
        });
        toast.success("✓ تم التحديث");
      } else {
        await api.post("/admin/pools", {
          name: poolForm.name, description: poolForm.description || "",
          amount_per_round: Number(poolForm.amount_per_round), member_count: Number(poolForm.member_count),
          frequency: "daily",
        });
        toast.success("✓ تم الإنشاء");
      }
      setPoolForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };
  const deletePool = async (pool_id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه السلفة؟")) return;
    try { await api.delete(`/admin/pools/${pool_id}`);
      toast.success("تم الحذف"); load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  const tierBadge = (tier) => {
    const m = { bronze: "bg-orange-700/30 text-orange-300", silver: "bg-slate-500/30 text-slate-200", gold: "bg-[#D4AF37]/30 text-[#D4AF37]" };
    return <span className={`text-[10px] px-2 py-0.5 rounded ${m[tier] || "bg-[#1A2D4D] text-[#8FA3BD]"}`}>{tier}</span>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient mb-8 flex items-center gap-2">
        <ShieldCheck className="w-7 h-7 text-[#00E676]" /> {t("admin")}
      </h1>

      <div className="flex gap-2 mb-6 border-b border-[#1A2D4D] overflow-x-auto">
        {[
          { id: "search", label: "بحث 🔍" },
          { id: "deposits", label: "الإيداعات", count: deposits.filter((d) => d.status === "pending").length },
          { id: "withdrawals", label: "السحوبات", count: withdrawals.filter((w) => w.status === "pending").length },
          { id: "kyc", label: "KYC", count: pending.length },
          { id: "promos", label: "إعلانات", count: promos.filter((p) => p.status === "pending").length },
          { id: "users", label: "المستخدمين", count: revenue.total_users },
          { id: "ledger", label: "سجل المعاملات 📒" },
          { id: "referrals", label: "الإحالات 🎁" },
          { id: "fallback", label: "فيديوهات احتياطية 🎬" },
          { id: "pools", label: "السلف", count: pools.length },
          { id: "revenue", label: "الإيرادات" },
          { id: "rev-wallet", label: "محفظة الإيرادات" },
        ].map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`tab-${tb.id}`}
            className={`px-4 py-3 text-sm whitespace-nowrap flex items-center gap-2 ${tab === tb.id ? "text-[#00E5FF] border-b-2 border-[#00E5FF]" : "text-[#8FA3BD]"}`}>
            {tb.label}
            {tb.count > 0 && <span className="text-[10px] bg-[#FF9500] text-[#050E1A] px-1.5 rounded-full">{tb.count}</span>}
          </button>
        ))}
      </div>

      {/* SEARCH TAB */}
      {tab === "search" && (
        <div>
          <div className="bento-card p-4 mb-5 flex gap-2">
            <Input placeholder="ابحث: اسم، إيميل، رقم هاتف..." value={search.query}
              onChange={(e) => setSearch({ ...search, query: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              className="bg-[#050E1A] border-[#1A2D4D]" data-testid="search-input" />
            <Button onClick={doSearch} className="btn-cyan" data-testid="search-btn">بحث</Button>
          </div>
          <div className="bento-card p-2 table-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8FA3BD]">
                <tr><th className="p-3 text-start">الاسم</th><th className="p-3 text-start">البريد</th><th className="p-3 text-start">الهاتف</th><th className="p-3 text-start">المستوى</th><th className="p-3 text-start">الرصيد</th><th className="p-3 text-end"></th></tr>
              </thead>
              <tbody>
                {search.results.map((u) => (
                  <tr key={u.user_id} className="border-t border-[#1A2D4D]" data-testid={`search-row-${u.user_id}`}>
                    <td className="p-3">{u.nickname || u.name}<div className="text-xs text-[#8FA3BD]">{u.real_name || "—"}</div></td>
                    <td className="p-3 text-[#8FA3BD] text-xs">{u.email}</td>
                    <td className="p-3 text-[#8FA3BD] text-xs">{u.phone || "—"}</td>
                    <td className="p-3">{tierBadge(u.tier)}</td>
                    <td className="p-3 text-[#00E5FF]">${(u.balance || 0).toFixed(2)}</td>
                    <td className="p-3 text-end">
                      <Button size="sm" variant="outline" className="border-[#1A2D4D]" onClick={() => openUserDetail(u.user_id)} data-testid={`open-detail-${u.user_id}`}>
                        <Eye className="w-3 h-3 me-1" /> تفاصيل
                      </Button>
                    </td>
                  </tr>
                ))}
                {search.results.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-[#4A5970]">اكتب اسم أو إيميل واضغط "بحث"</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DEPOSITS TAB */}
      {tab === "deposits" && (
        <div className="bento-card p-2 table-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs text-[#8FA3BD]">
              <tr><th className="p-3 text-start">User</th><th className="p-3 text-start">Method</th><th className="p-3 text-end">Amount</th><th className="p-3 text-start">Status</th><th className="p-3 text-end">Action</th></tr>
            </thead>
            <tbody>
              {deposits.map((d) => (
                <tr key={d.deposit_id} className="border-t border-[#1A2D4D]" data-testid={`dep-row-${d.deposit_id}`}>
                  <td className="p-3">{d.user_name}<div className="text-xs text-[#8FA3BD]">{d.user_email}</div></td>
                  <td className="p-3 text-[#8FA3BD]">{d.method}</td>
                  <td className="p-3 text-end text-[#00E5FF] font-bold">${d.amount.toFixed(2)}</td>
                  <td className="p-3 text-xs">
                    <span className={`px-2 py-0.5 rounded ${d.status === "approved" ? "bg-[#00E676]/15 text-[#00E676]" : d.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-[#FF9500]/15 text-[#FF9500]"}`}>{d.status}</span>
                    {d.admin_note && <div className="text-[10px] text-[#8FA3BD] mt-1">{d.admin_note}</div>}
                  </td>
                  <td className="p-3 text-end">
                    {d.status === "pending" && (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" className="btn-cyan" onClick={() => approveDep(d.deposit_id)} data-testid={`dep-approve-${d.deposit_id}`}><Check className="w-3 h-3" /></Button>
                        <Button size="sm" variant="destructive" onClick={() => setRejectDep({ dep_id: d.deposit_id, note: "" })} data-testid={`dep-reject-${d.deposit_id}`}><X className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {deposits.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-[#4A5970]">لا توجد إيداعات</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* PROMOS TAB */}
      {tab === "promos" && (
        <div className="bento-card p-2 table-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs text-[#8FA3BD]">
              <tr><th className="p-3 text-start">User</th><th className="p-3 text-start">Video</th><th className="p-3 text-start">Days</th><th className="p-3 text-end">Budget</th><th className="p-3 text-start">Status</th><th className="p-3 text-end">Action</th></tr>
            </thead>
            <tbody>
              {promos.map((p) => (
                <tr key={p.promo_id} className="border-t border-[#1A2D4D]" data-testid={`promo-row-${p.promo_id}`}>
                  <td className="p-3">{p.user_name}</td>
                  <td className="p-3"><a href={p.video_url} target="_blank" rel="noreferrer" className="text-[#00E5FF] underline text-xs truncate inline-block max-w-[200px]">{p.video_url}</a></td>
                  <td className="p-3 text-[#8FA3BD]">{p.days}</td>
                  <td className="p-3 text-end text-[#00E5FF] font-bold">${p.budget.toFixed(2)}</td>
                  <td className="p-3 text-xs">
                    <span className={`px-2 py-0.5 rounded ${p.status === "active" ? "bg-[#00E676]/15 text-[#00E676]" : p.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-[#FF9500]/15 text-[#FF9500]"}`}>{p.status}</span>
                    {p.admin_note && <div className="text-[10px] text-[#8FA3BD] mt-1">{p.admin_note}</div>}
                  </td>
                  <td className="p-3 text-end">
                    {p.status === "pending" && (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" className="btn-cyan" onClick={() => approvePromo(p.promo_id)} data-testid={`promo-approve-${p.promo_id}`}><Check className="w-3 h-3" /></Button>
                        <Button size="sm" variant="destructive" onClick={() => setRejectPromo({ promo_id: p.promo_id, note: "" })} data-testid={`promo-reject-${p.promo_id}`}><X className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {promos.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-[#4A5970]">لا توجد طلبات إعلانات</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* REV-WALLET TAB */}
      {tab === "rev-wallet" && (
        <div>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="bento-card p-6">
              <div className="text-xs text-[#8FA3BD]">إجمالي الإيرادات</div>
              <div className="text-3xl font-bold text-cyan-gradient" data-testid="rev-total">${revWallet.total_revenue.toFixed(2)}</div>
            </div>
            <div className="bento-card p-6">
              <div className="text-xs text-[#8FA3BD]">المسحوب</div>
              <div className="text-3xl font-bold text-[#FF9500]" data-testid="rev-withdrawn">${revWallet.withdrawn.toFixed(2)}</div>
            </div>
            <div className="bento-card p-6 border-[#00E676]/40">
              <div className="text-xs text-[#8FA3BD]">المتاح للسحب</div>
              <div className="text-3xl font-bold text-[#00E676]" data-testid="rev-available">${revWallet.available.toFixed(2)}</div>
              <div className="flex gap-2 mt-3">
                <Button onClick={() => setRevWithdraw({ amount: "", method: "usdt", destination: "" })} className="btn-cyan flex-1" data-testid="open-rev-withdraw" disabled={revWallet.available <= 0}>
                  <ArrowUpCircle className="w-4 h-4 me-1.5" /> سحب
                </Button>
                <Button onClick={verifyRevenue} variant="outline" className="border-[#1A2D4D]" data-testid="verify-revenue-btn">
                  <ShieldCheck className="w-4 h-4 me-1" /> تحقق
                </Button>
              </div>
            </div>
          </div>
          {/* Buckets card per user request */}
          <div className="bento-card p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">💰 العمولات حسب النوع</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-[#1A2D4D]/50 py-1"><span className="text-[#8FA3BD]">عمولات الإيداع:</span><span className="text-[#00E676] font-bold" data-testid="bucket-deposit">${(revWallet.buckets?.deposit || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-b border-[#1A2D4D]/50 py-1"><span className="text-[#8FA3BD]">عمولات السحب:</span><span className="text-[#00E676] font-bold" data-testid="bucket-withdraw">${(revWallet.buckets?.withdraw || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-b border-[#1A2D4D]/50 py-1"><span className="text-[#8FA3BD]">عمولات الجمعية:</span><span className="text-[#00E676] font-bold" data-testid="bucket-pool">${(revWallet.buckets?.pool || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-b border-[#1A2D4D]/50 py-1"><span className="text-[#8FA3BD]">عمولات النقاط:</span><span className="text-[#00E676] font-bold" data-testid="bucket-points">${(revWallet.buckets?.points || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-b border-[#1A2D4D]/50 py-1"><span className="text-[#8FA3BD]">إيداعات الترويج (تروي):</span><span className="text-[#00E676] font-bold" data-testid="bucket-promo">${(revWallet.buckets?.promo || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-b border-[#1A2D4D]/50 py-1"><span className="text-[#8FA3BD]">مشاهدات الإعلانات:</span><span className="text-[#00E676] font-bold" data-testid="bucket-adview">${(revWallet.buckets?.ad_view || 0).toFixed(2)}</span></div>
              <div className="flex justify-between py-2 mt-2 border-t-2 border-[#00E676]/40">
                <span className="text-white font-bold">الإجمالي:</span>
                <span className="text-cyan-gradient text-xl font-bold">${revWallet.total_revenue.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-[#FF9500]">المتبقي للتحويل:</span>
                <span className="text-[#FF9500] font-bold">${revWallet.available.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-3">سجل سحوبات الإيرادات</h3>
          <div className="bento-card p-2 table-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8FA3BD]"><tr><th className="p-3 text-start">When</th><th className="p-3 text-start">Method</th><th className="p-3 text-start">Destination</th><th className="p-3 text-end">Amount</th><th className="p-3 text-start">Status</th></tr></thead>
              <tbody>
                {revWallet.history.map((h) => (
                  <tr key={h.rwd_id} className="border-t border-[#1A2D4D]">
                    <td className="p-3 text-xs text-[#8FA3BD]">{(h.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 text-[#8FA3BD]">{h.method}</td>
                    <td className="p-3 text-xs break-all max-w-[200px]">{h.destination}</td>
                    <td className="p-3 text-end text-[#FF9500]">${h.amount.toFixed(2)}</td>
                    <td className="p-3"><span className="text-xs px-2 py-0.5 rounded bg-[#FF9500]/15 text-[#FF9500]">{h.status}</span></td>
                  </tr>
                ))}
                {revWallet.history.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-[#4A5970]">—</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Broadcast button (above KYC tab) */}
      {tab === "users" && (
        <div className="mb-4">
          <Button onClick={() => setNotifyForm({ title: "", body: "", target_user_id: null })} className="btn-cyan" data-testid="broadcast-btn">
            <Send className="w-4 h-4 me-1.5" /> إرسال إشعار للجميع
          </Button>
        </div>
      )}

      {/* KYC TAB */}
      {tab === "kyc" && (
        <div className="bento-card p-2 table-scroll mb-6">
          <table className="w-full text-sm">
            <thead className="text-xs text-[#8FA3BD]">
              <tr><th className="p-3 text-start">User</th><th className="p-3 text-start">Doc</th><th className="p-3 text-start">When</th><th className="p-3 text-end">Action</th></tr>
            </thead>
            <tbody>
              {pending.map((k) => (
                <tr key={k.kyc_id} className="border-t border-[#1A2D4D]" data-testid={`kyc-row-${k.kyc_id}`}>
                  <td className="p-3 text-xs">{k.user_name} <div className="text-[10px] text-[#8FA3BD]">{k.user_email}</div></td>
                  <td className="p-3 text-xs"><span className="px-2 py-0.5 rounded bg-[#00E5FF]/10 text-[#00E5FF]">{k.doc_type}</span></td>
                  <td className="p-3 text-xs text-[#8FA3BD]">{(k.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3 text-end flex gap-2 justify-end">
                    <Button size="sm" variant="outline" className="border-[#1A2D4D]" onClick={() => previewKycDoc(k.kyc_id)} data-testid={`preview-${k.kyc_id}`}><Eye className="w-3 h-3" /></Button>
                    <Button size="sm" className="btn-cyan" onClick={() => approveKyc(k.kyc_id)} data-testid={`approve-${k.kyc_id}`}><Check className="w-3 h-3" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => setRejectKyc({ kyc_id: k.kyc_id, note: "" })} data-testid={`reject-${k.kyc_id}`}><X className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
              {pending.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-[#4A5970]">لا توجد طلبات معلّقة</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* WITHDRAWALS TAB */}
      {tab === "withdrawals" && (
        <div className="bento-card p-2 table-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs text-[#8FA3BD]">
              <tr><th className="p-3 text-start">User</th><th className="p-3 text-start">Method</th><th className="p-3 text-start">Destination</th><th className="p-3 text-end">Amount</th><th className="p-3 text-start">Status</th><th className="p-3 text-end">Action</th></tr>
            </thead>
            <tbody>
              {withdrawals.map((w) => (
                <tr key={w.withdrawal_id} className="border-t border-[#1A2D4D]" data-testid={`wd-row-${w.withdrawal_id}`}>
                  <td className="p-3">{w.user_name}<div className="text-xs text-[#8FA3BD]">{w.user_email}</div></td>
                  <td className="p-3 text-[#8FA3BD]">{w.method}</td>
                  <td className="p-3 text-xs text-[#8FA3BD] max-w-[200px] truncate">{w.destination}</td>
                  <td className="p-3 text-end text-[#00E5FF] font-bold">${w.amount.toFixed(2)}</td>
                  <td className="p-3 text-xs">
                    <span className={`px-2 py-0.5 rounded ${w.status === "approved" ? "bg-[#00E676]/15 text-[#00E676]" : w.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-[#FF9500]/15 text-[#FF9500]"}`}>{w.status}</span>
                    {w.admin_note && <div className="text-[10px] text-[#8FA3BD] mt-1 max-w-[150px] truncate">{w.admin_note}</div>}
                  </td>
                  <td className="p-3 text-end">
                    {w.status === "pending" && (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" className="btn-cyan" onClick={() => approveWd(w.withdrawal_id)} data-testid={`wd-approve-${w.withdrawal_id}`}><Check className="w-3 h-3" /></Button>
                        <Button size="sm" variant="destructive" onClick={() => setRejectWd({ wd_id: w.withdrawal_id, note: "" })} data-testid={`wd-reject-${w.withdrawal_id}`}><X className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {withdrawals.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-[#4A5970]">لا سحوبات</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* USERS TAB */}
      {tab === "users" && (
        <div className="bento-card p-2 table-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs text-[#8FA3BD]">
              <tr>
                <th className="p-3 text-start">الاسم</th><th className="p-3 text-start">البريد</th>
                <th className="p-3 text-start">المستوى</th><th className="p-3 text-start">XP</th>
                <th className="p-3 text-start">KYC</th><th className="p-3 text-start">الرصيد</th>
                <th className="p-3 text-start">السلف</th><th className="p-3 text-end">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className={`border-t border-[#1A2D4D] ${u.is_blocked ? "opacity-50" : ""}`} data-testid={`user-row-${u.user_id}`}>
                  <td className="p-3">
                    {u.name}
                    {u.is_blocked && <span className="ms-2 text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">محظور</span>}
                    {u.role === "admin" && <span className="ms-2 text-[10px] bg-[#D4AF37]/20 text-[#D4AF37] px-1.5 py-0.5 rounded">Admin</span>}
                  </td>
                  <td className="p-3 text-[#8FA3BD] text-xs">{u.email}</td>
                  <td className="p-3">{tierBadge(u.tier)}</td>
                  <td className="p-3 text-[#FF9500]">{(u.xp || 0).toLocaleString()}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.kyc_status === "approved" ? "bg-[#00E676]/15 text-[#00E676]" : "bg-[#1A2D4D] text-[#8FA3BD]"}`}>{u.kyc_status}</span></td>
                  <td className="p-3 text-[#00E5FF]">${(u.balance || 0).toFixed(2)}</td>
                  <td className="p-3 text-xs text-[#8FA3BD]">{u.pools_joined_count || 0}</td>
                  <td className="p-3 text-end">
                    {u.role !== "admin" && (
                      <div className="flex gap-2 justify-end flex-wrap">
                        <Button size="sm" variant="outline" className="border-[#1A2D4D] text-[#00E5FF]" onClick={() => setAdjustUser({ user_id: u.user_id, name: u.name, amount: "", reason: "" })} data-testid={`adjust-${u.user_id}`} title="تعديل الرصيد">
                          <DollarSign className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" className="border-[#1A2D4D] text-[#00E676]" onClick={() => setNotifyForm({ title: "", body: "", target_user_id: u.user_id, target_name: u.name })} data-testid={`notify-${u.user_id}`} title="إرسال إشعار">
                          <Send className="w-3 h-3" />
                        </Button>
                        {u.kyc_status !== "approved" ? (
                          <Button size="sm" variant="outline" className="border-[#1A2D4D] text-[#D4AF37]" onClick={() => kycSkip(u.user_id)} data-testid={`kyc-skip-${u.user_id}`} title="تخطّي KYC (قبول يدوي)">
                            <ShieldCheck className="w-3 h-3" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="border-[#1A2D4D] text-[#8FA3BD]" onClick={() => kycReset(u.user_id)} data-testid={`kyc-reset-${u.user_id}`} title="إعادة تعيين KYC">
                            <ShieldCheck className="w-3 h-3 opacity-50" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-[#1A2D4D] text-[#FF9500]" onClick={() => toggleBlock(u)} data-testid={`block-${u.user_id}`} title="حظر">
                          <Ban className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setConfirmDeleteUser(u)} data-testid={`delete-${u.user_id}`} title="حذف">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* LEDGER TAB - Master Transaction Log with search */}
      {tab === "ledger" && (
        <div>
          <div className="bento-card p-4 mb-5 flex flex-wrap gap-2 items-center">
            <Input placeholder="ابحث: Order ID، اسم، إيميل، هاتف..." value={ledger.query}
              onChange={(e) => setLedger({ ...ledger, query: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && loadLedger()}
              className="bg-[#050E1A] border-[#1A2D4D] flex-1 min-w-[200px]" data-testid="ledger-search-input" />
            <select value={ledger.type} onChange={(e) => setLedger({ ...ledger, type: e.target.value })}
              className="bg-[#050E1A] border border-[#1A2D4D] rounded-md px-3 py-2 text-sm text-white">
              <option value="">كل الأنواع</option>
              <option value="deposit_request">إيداع</option>
              <option value="withdraw_request">سحب</option>
              <option value="pool_winnings">دور سلفة</option>
              <option value="pool_escrow">حجز سلفة</option>
              <option value="coin_redeem">تحويل نقاط</option>
              <option value="free_gift">هدية مجانية</option>
              <option value="referral_inviter_bonus">عمولة إحالة</option>
              <option value="referral_invitee_bonus">بونص مدعو</option>
              <option value="admin_adjust">تعديل أدمن</option>
            </select>
            <Button onClick={loadLedger} className="btn-cyan" data-testid="ledger-search-btn">بحث</Button>
          </div>
          <div className="text-xs text-[#8FA3BD] mb-2">إجمالي النتائج: {ledger.rows.length}</div>
          <div className="bento-card p-2 table-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8FA3BD]">
                <tr>
                  <th className="p-3 text-start">Order ID</th>
                  <th className="p-3 text-start">User</th>
                  <th className="p-3 text-start">Type</th>
                  <th className="p-3 text-start">Method</th>
                  <th className="p-3 text-end">Amount</th>
                  <th className="p-3 text-start">Status</th>
                  <th className="p-3 text-start">Date</th>
                </tr>
              </thead>
              <tbody>
                {ledger.rows.map((tx) => (
                  <tr key={tx.tx_id} className="border-t border-[#1A2D4D]" data-testid={`ledger-row-${tx.tx_id}`}>
                    <td className="p-3 text-[10px] text-[#00E5FF] font-mono whitespace-nowrap">{tx.order_id || "—"}</td>
                    <td className="p-3 whitespace-nowrap">{tx.user_name || "—"}<div className="text-[10px] text-[#8FA3BD]">{tx.user_email}</div></td>
                    <td className="p-3 text-xs whitespace-nowrap">{tx.type}</td>
                    <td className="p-3 text-[#8FA3BD] text-xs">{tx.method || "—"}</td>
                    <td className={`p-3 text-end font-bold whitespace-nowrap ${tx.amount > 0 ? "text-[#00E676]" : "text-[#FF9500]"}`}>${(tx.amount || 0).toFixed(2)}</td>
                    <td className="p-3 text-xs"><span className={`px-2 py-0.5 rounded ${tx.status === "completed" || tx.status === "approved" ? "bg-[#00E676]/15 text-[#00E676]" : tx.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-[#FF9500]/15 text-[#FF9500]"}`}>{tx.status}</span></td>
                    <td className="p-3 text-[10px] text-[#4A5970] whitespace-nowrap">{(tx.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
                {ledger.rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-[#4A5970]">لا توجد معاملات</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* REFERRALS TAB - Affiliate stats */}
      {tab === "referrals" && (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="bento-card p-4">
              <div className="text-xs text-[#8FA3BD]">إجمالي المدعوين</div>
              <div className="text-2xl font-bold text-cyan-gradient" data-testid="refs-total">{refs.length}</div>
            </div>
            <div className="bento-card p-4">
              <div className="text-xs text-[#8FA3BD]">أودعوا</div>
              <div className="text-2xl font-bold text-[#00E676]" data-testid="refs-deposited">{refs.filter((r) => r.has_deposited).length}</div>
            </div>
            <div className="bento-card p-4">
              <div className="text-xs text-[#8FA3BD]">إجمالي العمولات</div>
              <div className="text-2xl font-bold text-[#FF9500]" data-testid="refs-commission">${refs.reduce((s, r) => s + (r.reward_paid || 0), 0).toFixed(2)}</div>
            </div>
            <div className="bento-card p-4">
              <div className="text-xs text-[#8FA3BD]">إيداعات أول مرة</div>
              <div className="text-2xl font-bold text-[#D4AF37]">${refs.reduce((s, r) => s + (r.first_deposit_amount || 0), 0).toFixed(2)}</div>
            </div>
          </div>
          <div className="bento-card p-2 table-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8FA3BD]">
                <tr>
                  <th className="p-3 text-start">المدعو (Invitee)</th>
                  <th className="p-3 text-start">الداعي (Inviter)</th>
                  <th className="p-3 text-start">تاريخ التسجيل</th>
                  <th className="p-3 text-start">أودع؟</th>
                  <th className="p-3 text-end">قيمة أول إيداع</th>
                  <th className="p-3 text-end">عمولة 10% للداعي</th>
                </tr>
              </thead>
              <tbody>
                {refs.map((r) => (
                  <tr key={r.invitee_id} className="border-t border-[#1A2D4D]" data-testid={`ref-row-${r.invitee_id}`}>
                    <td className="p-3 whitespace-nowrap">{r.invitee_name}<div className="text-[10px] text-[#8FA3BD]">{r.invitee_email}</div></td>
                    <td className="p-3 whitespace-nowrap">{r.inviter_name}<div className="text-[10px] text-[#8FA3BD]">{r.inviter_email}</div></td>
                    <td className="p-3 text-[10px] text-[#4A5970] whitespace-nowrap">{(r.registered_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-xs">
                      {r.has_deposited ? <span className="px-2 py-0.5 rounded bg-[#00E676]/15 text-[#00E676]">نعم</span> : <span className="px-2 py-0.5 rounded bg-[#FF9500]/15 text-[#FF9500]">لا</span>}
                    </td>
                    <td className="p-3 text-end text-[#00E5FF] font-bold">${(r.first_deposit_amount || 0).toFixed(2)}</td>
                    <td className="p-3 text-end text-[#00E676] font-bold">${(r.reward_paid || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {refs.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-[#4A5970]">لا توجد إحالات بعد</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FALLBACK VIDEOS TAB */}
      {tab === "fallback" && (
        <div>
          <div className="bento-card p-5 mb-5 border-[#D4AF37]/30">
            <h3 className="text-lg font-semibold mb-2 text-[#D4AF37]">🎬 فيديوهات احتياطية للإعلانات</h3>
            <p className="text-xs text-[#8FA3BD] mb-4">
              تُعرض هذه الفيديوهات في صفحة "اربح" عندما لا توجد إعلانات ترويجية نشطة. تعمل كاحتياطي للحفاظ على تجربة المستخدمين.
            </p>
            <div className="grid sm:grid-cols-3 gap-2">
              <Input value={fbForm.url} onChange={(e) => setFbForm({ ...fbForm, url: e.target.value })}
                placeholder="https://youtube.com/watch?v=... أو tiktok.com/.../video/..."
                className="bg-[#050E1A] border-[#1A2D4D] sm:col-span-2" data-testid="fallback-url-input" />
              <select value={fbForm.platform} onChange={(e) => setFbForm({ ...fbForm, platform: e.target.value })}
                className="bg-[#050E1A] border border-[#1A2D4D] rounded-md px-3 text-sm text-white" data-testid="fallback-platform-select">
                <option value="youtube">YouTube</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>
            <Input value={fbForm.label} onChange={(e) => setFbForm({ ...fbForm, label: e.target.value })}
              placeholder="تسمية اختيارية (مثلاً: محتوى تعليمي)"
              className="bg-[#050E1A] border-[#1A2D4D] mt-2" data-testid="fallback-label-input" />
            <Button onClick={addFallback} className="btn-cyan mt-3" data-testid="add-fallback-btn">
              <Plus className="w-4 h-4 me-1" /> إضافة فيديو احتياطي
            </Button>
          </div>
          <div className="bento-card p-2 table-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8FA3BD]">
                <tr>
                  <th className="p-3 text-start">المنصة</th>
                  <th className="p-3 text-start">الرابط</th>
                  <th className="p-3 text-start">التسمية</th>
                  <th className="p-3 text-end">مشاهدات</th>
                  <th className="p-3 text-start">الحالة</th>
                  <th className="p-3 text-end">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {fallbacks.map((fv) => (
                  <tr key={fv.fv_id} className="border-t border-[#1A2D4D]" data-testid={`fallback-row-${fv.fv_id}`}>
                    <td className="p-3 text-xs">
                      <span className={`px-2 py-0.5 rounded ${fv.platform === "tiktok" ? "bg-[#FF1493]/15 text-[#FF1493]" : "bg-red-500/15 text-red-400"}`}>
                        {fv.platform}
                      </span>
                    </td>
                    <td className="p-3 text-xs">
                      <a href={fv.url} target="_blank" rel="noreferrer" className="text-[#00E5FF] underline truncate inline-block max-w-[220px]">{fv.url}</a>
                    </td>
                    <td className="p-3 text-xs text-[#8FA3BD]">{fv.label || "—"}</td>
                    <td className="p-3 text-end text-[#00E5FF]">{fv.views_count || 0}</td>
                    <td className="p-3 text-xs">
                      <span className={`px-2 py-0.5 rounded ${fv.is_active ? "bg-[#00E676]/15 text-[#00E676]" : "bg-red-500/15 text-red-400"}`}>
                        {fv.is_active ? "نشط" : "موقوف"}
                      </span>
                    </td>
                    <td className="p-3 text-end flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="border-[#1A2D4D]" onClick={() => toggleFallback(fv.fv_id)} data-testid={`toggle-fallback-${fv.fv_id}`}>
                        {fv.is_active ? "إيقاف" : "تفعيل"}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => deleteFallback(fv.fv_id)} data-testid={`del-fallback-${fv.fv_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {fallbacks.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-[#4A5970]">لا توجد فيديوهات احتياطية بعد</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* POOLS TAB */}
      {tab === "pools" && (
        <div>
          <Button className="btn-cyan mb-4" onClick={() => setPoolForm({ name: "", amount_per_round: 5, member_count: 10, description: "" })} data-testid="add-pool-btn">
            <Plus className="w-4 h-4 me-1" /> إضافة سلفة
          </Button>
          <div className="bento-card p-2 table-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8FA3BD]">
                <tr><th className="p-3 text-start">الاسم</th><th className="p-3 text-start">المبلغ</th><th className="p-3 text-start">الأعضاء</th><th className="p-3 text-start">الحالة</th><th className="p-3 text-end">إجراء</th></tr>
              </thead>
              <tbody>
                {pools.map((p) => (
                  <tr key={p.pool_id} className="border-t border-[#1A2D4D]" data-testid={`pool-row-${p.pool_id}`}>
                    <td className="p-3">
                      {p.name}
                      {p.is_admin_pool && <span className="ms-2 text-[10px] bg-[#D4AF37]/20 text-[#D4AF37] px-1.5 py-0.5 rounded">Official</span>}
                    </td>
                    <td className="p-3 text-[#00E5FF]">${p.amount_per_round}/يوم</td>
                    <td className="p-3 text-[#8FA3BD]">{p.members?.length || 0}/{p.member_count}</td>
                    <td className="p-3 text-xs">{p.status}</td>
                    <td className="p-3 text-end flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="border-[#1A2D4D]" onClick={() => setPoolForm({ pool_id: p.pool_id, name: p.name, amount_per_round: p.amount_per_round, member_count: p.member_count, description: p.description || "" })} data-testid={`edit-pool-${p.pool_id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => deletePool(p.pool_id)} data-testid={`del-pool-${p.pool_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {pools.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-[#4A5970]">لا توجد سلف</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* REVENUE TAB */}
      {tab === "revenue" && (
        <div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bento-card p-6">
              <DollarSign className="w-6 h-6 text-[#00E676] mb-3" />
              <div className="text-xs text-[#8FA3BD]">إجمالي الإيداعات</div>
              <div className="text-2xl font-bold text-cyan-gradient" data-testid="total-deposits">${(revenue.total_deposits_amount || 0).toFixed(2)}</div>
              <div className="text-xs text-[#4A5970] mt-1">{revenue.total_deposits_count || 0} عملية</div>
            </div>
            <div className="bento-card p-6">
              <TrendingUp className="w-6 h-6 text-[#00E5FF] mb-3" />
              <div className="text-xs text-[#8FA3BD]">رسوم وعمولات</div>
              <div className="text-2xl font-bold text-white" data-testid="revenue-total">${revenue.grand_total.toFixed(2)}</div>
            </div>
            <div className="bento-card p-6">
              <CoinsIcon className="w-6 h-6 text-[#FF9500] mb-3" />
              <div className="text-xs text-[#8FA3BD]">مشاهدات الإعلانات</div>
              <div className="text-2xl font-bold text-white" data-testid="total-ad-views">{(revenue.total_ad_views || 0).toLocaleString()}</div>
            </div>
            <div className="bento-card p-6">
              <Users className="w-6 h-6 text-[#D4AF37] mb-3" />
              <div className="text-xs text-[#8FA3BD]">المستخدمين</div>
              <div className="text-2xl font-bold text-white">{revenue.total_users || 0}</div>
              <div className="text-xs text-red-400 mt-1">{revenue.total_blocked || 0} محظور</div>
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-3">تفصيل الإيرادات</h3>
          <div className="bento-card p-2 table-scroll mb-6">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8FA3BD]"><tr><th className="p-3 text-start">النوع</th><th className="p-3 text-start">العدد</th><th className="p-3 text-end">المجموع</th></tr></thead>
              <tbody>
                {revenue.by_event.map((e) => (
                  <tr key={e.event} className="border-t border-[#1A2D4D]">
                    <td className="p-3">{e.event}</td>
                    <td className="p-3 text-[#8FA3BD]">{e.count}</td>
                    <td className="p-3 text-end text-[#00E676]">${e.total.toFixed(2)}</td>
                  </tr>
                ))}
                {revenue.by_event.length === 0 && <tr><td colSpan="3" className="p-6 text-center text-[#4A5970]">لا توجد إيرادات</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reject deposit dialog */}
      <Dialog open={!!rejectDep} onOpenChange={(o) => !o && setRejectDep(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader><DialogTitle>سبب رفض الإيداع</DialogTitle><DialogDescription>سيُرسل للمستخدم</DialogDescription></DialogHeader>
          <Input value={rejectDep?.note || ""} onChange={(e) => setRejectDep({ ...rejectDep, note: e.target.value })} placeholder="مثلاً: المبلغ لم يصل" className="bg-[#050E1A] border-[#1A2D4D]" data-testid="reject-dep-note" />
          <DialogFooter><Button variant="destructive" onClick={submitRejectDep} data-testid="confirm-reject-dep">تأكيد الرفض</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject promo dialog */}
      <Dialog open={!!rejectPromo} onOpenChange={(o) => !o && setRejectPromo(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader><DialogTitle>سبب رفض الإعلان</DialogTitle><DialogDescription>يُرد المبلغ للمعلن</DialogDescription></DialogHeader>
          <Input value={rejectPromo?.note || ""} onChange={(e) => setRejectPromo({ ...rejectPromo, note: e.target.value })} placeholder="مثلاً: محتوى غير مناسب" className="bg-[#050E1A] border-[#1A2D4D]" data-testid="reject-promo-note" />
          <DialogFooter><Button variant="destructive" onClick={submitRejectPromo} data-testid="confirm-reject-promo">تأكيد الرفض</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Balance adjust dialog */}
      <Dialog open={!!adjustUser} onOpenChange={(o) => !o && setAdjustUser(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader>
            <DialogTitle>تعديل رصيد: {adjustUser?.name}</DialogTitle>
            <DialogDescription>موجب = إضافة • سالب = خصم (مثلاً: -10)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-[#8FA3BD]">المبلغ (USD)</Label>
              <Input type="number" value={adjustUser?.amount || ""} onChange={(e) => setAdjustUser({ ...adjustUser, amount: e.target.value })} placeholder="10 أو -10" className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="adjust-amount" /></div>
            <div><Label className="text-xs text-[#8FA3BD]">السبب</Label>
              <Input value={adjustUser?.reason || ""} onChange={(e) => setAdjustUser({ ...adjustUser, reason: e.target.value })} placeholder="مثلاً: تصحيح إيداع مفقود" className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="adjust-reason" /></div>
          </div>
          <DialogFooter><Button className="btn-cyan" onClick={submitAdjust} data-testid="confirm-adjust">تثبيت</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revenue withdraw dialog */}
      <Dialog open={!!revWithdraw} onOpenChange={(o) => !o && setRevWithdraw(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader>
            <DialogTitle>سحب إيرادات المنصة</DialogTitle>
            <DialogDescription>المتاح: ${revWallet.available.toFixed(2)} — يُنفّذ يدوياً من OKX</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-[#8FA3BD]">المبلغ (USD)</Label>
              <Input type="number" value={revWithdraw?.amount || ""} onChange={(e) => setRevWithdraw({ ...revWithdraw, amount: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="rev-amount" /></div>
            <div><Label className="text-xs text-[#8FA3BD]">العملة</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setRevWithdraw({ ...revWithdraw, method: "trx" })}
                  className={`p-3 rounded-lg border text-sm ${revWithdraw?.method === "trx" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}>TRX</button>
                <button type="button" onClick={() => setRevWithdraw({ ...revWithdraw, method: "usdt" })}
                  className={`p-3 rounded-lg border text-sm ${revWithdraw?.method === "usdt" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}>USDT</button>
              </div>
            </div>
            <div><Label className="text-xs text-[#8FA3BD]">عنوان المحفظة (TRC20)</Label>
              <Input value={revWithdraw?.destination || ""} onChange={(e) => setRevWithdraw({ ...revWithdraw, destination: e.target.value })} placeholder="TXxxxxxxxxxxxxxxxx" className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="rev-dest" /></div>
          </div>
          <DialogFooter><Button className="btn-cyan" onClick={submitRevWithdraw} data-testid="confirm-rev-withdraw">تقديم</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify / Broadcast dialog */}
      <Dialog open={!!notifyForm} onOpenChange={(o) => !o && setNotifyForm(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader>
            <DialogTitle>{notifyForm?.target_user_id ? `رسالة إلى: ${notifyForm.target_name}` : "إشعار جماعي"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-[#8FA3BD]">العنوان</Label>
              <Input value={notifyForm?.title || ""} onChange={(e) => setNotifyForm({ ...notifyForm, title: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="notify-title" /></div>
            <div><Label className="text-xs text-[#8FA3BD]">النص</Label>
              <Input value={notifyForm?.body || ""} onChange={(e) => setNotifyForm({ ...notifyForm, body: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="notify-body" /></div>
          </div>
          <DialogFooter><Button className="btn-cyan" onClick={submitNotify} data-testid="confirm-notify"><Send className="w-3 h-3 me-1" /> إرسال</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KYC Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white max-w-3xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>معاينة: {previewDoc?.doc_type}</DialogTitle>
          </DialogHeader>
          {previewDoc?.mime_type?.includes("pdf") ? (
            <iframe
              src={`data:${previewDoc.mime_type};base64,${previewDoc.file_b64}`}
              className="w-full flex-1 bg-white rounded-lg"
              title="KYC Document"
              data-testid="kyc-preview-iframe"
            />
          ) : (
            <img src={`data:${previewDoc?.mime_type};base64,${previewDoc?.file_b64}`}
              alt="" className="max-w-full max-h-[60vh] object-contain mx-auto" />
          )}
          <DialogFooter><Button onClick={() => setPreviewDoc(null)}>إغلاق</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject KYC dialog */}
      <Dialog open={!!rejectKyc} onOpenChange={(o) => !o && setRejectKyc(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader><DialogTitle>سبب الرفض</DialogTitle><DialogDescription>سيُرسل للمستخدم في الإشعارات</DialogDescription></DialogHeader>
          <Input value={rejectKyc?.note || ""} onChange={(e) => setRejectKyc({ ...rejectKyc, note: e.target.value })}
            placeholder="مثلاً: المستند غير واضح" className="bg-[#050E1A] border-[#1A2D4D]" data-testid="reject-kyc-note" />
          <DialogFooter><Button variant="destructive" onClick={submitRejectKyc} data-testid="confirm-reject-kyc">تأكيد الرفض</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Withdrawal dialog */}
      <Dialog open={!!rejectWd} onOpenChange={(o) => !o && setRejectWd(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader><DialogTitle>سبب رفض السحب</DialogTitle><DialogDescription>سيُرسل للمستخدم — المبلغ يُعاد لرصيده</DialogDescription></DialogHeader>
          <Input value={rejectWd?.note || ""} onChange={(e) => setRejectWd({ ...rejectWd, note: e.target.value })}
            placeholder="مثلاً: العنوان غير صحيح" className="bg-[#050E1A] border-[#1A2D4D]" data-testid="reject-wd-note" />
          <DialogFooter><Button variant="destructive" onClick={submitRejectWd} data-testid="confirm-reject-wd">تأكيد الرفض</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user dialog */}
      <Dialog open={!!confirmDeleteUser} onOpenChange={(o) => !o && setConfirmDeleteUser(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader><DialogTitle>تأكيد الحذف</DialogTitle><DialogDescription className="text-red-400">سيتم حذف <span className="font-bold">{confirmDeleteUser?.name}</span> نهائياً (الرصيد، KYC، المعاملات).</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="border-[#1A2D4D] text-white" onClick={() => setConfirmDeleteUser(null)}>إلغاء</Button>
            <Button variant="destructive" onClick={submitDeleteUser} data-testid="confirm-delete-user">حذف نهائي</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pool form dialog */}
      <Dialog open={!!poolForm} onOpenChange={(o) => !o && setPoolForm(null)}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
          <DialogHeader><DialogTitle>{poolForm?.pool_id ? "تعديل السلفة" : "سلفة جديدة"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-[#8FA3BD]">الاسم</Label>
              <Input value={poolForm?.name || ""} onChange={(e) => setPoolForm({ ...poolForm, name: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="pool-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#8FA3BD]">المبلغ يومياً ($)</Label>
                <Input type="number" value={poolForm?.amount_per_round || ""} onChange={(e) => setPoolForm({ ...poolForm, amount_per_round: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="pool-amount" /></div>
              <div><Label className="text-xs text-[#8FA3BD]">عدد الأعضاء</Label>
                <Input type="number" value={poolForm?.member_count || ""} onChange={(e) => setPoolForm({ ...poolForm, member_count: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" data-testid="pool-members" /></div>
            </div>
            <div><Label className="text-xs text-[#8FA3BD]">الوصف</Label>
              <Input value={poolForm?.description || ""} onChange={(e) => setPoolForm({ ...poolForm, description: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] mt-1" /></div>
          </div>
          <DialogFooter><Button className="btn-cyan" onClick={submitPool} data-testid="save-pool-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User detail dialog with commission breakdown */}
      <Dialog open={!!search.detail} onOpenChange={(o) => !o && setSearch((s) => ({ ...s, detail: null }))}>
        <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white max-w-2xl">
          {search.detail && (
            <>
              <DialogHeader>
                <DialogTitle>👤 {search.detail.user.nickname || search.detail.user.name}</DialogTitle>
                <DialogDescription>{search.detail.user.email} • {search.detail.user.phone || "—"}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bento-card p-3"><div className="text-[10px] text-[#8FA3BD]">المستوى</div><div className="font-bold">{search.detail.user.tier_label_ar} ({search.detail.user.tier})</div></div>
                <div className="bento-card p-3"><div className="text-[10px] text-[#8FA3BD]">XP</div><div className="font-bold text-[#FF9500]">{search.detail.user.xp.toLocaleString()}</div></div>
                <div className="bento-card p-3"><div className="text-[10px] text-[#8FA3BD]">الرصيد</div><div className="font-bold text-[#00E5FF]">${search.detail.user.balance.toFixed(2)}</div></div>
                <div className="bento-card p-3"><div className="text-[10px] text-[#8FA3BD]">إجمالي الإيداعات</div><div className="font-bold text-[#00E676]">${search.detail.deposits_total.toFixed(2)} ({search.detail.deposits_count})</div></div>
                <div className="bento-card p-3"><div className="text-[10px] text-[#8FA3BD]">السلف</div><div className="font-bold">{search.detail.pools_joined} ({search.detail.pools_won} فاز)</div></div>
                <div className="bento-card p-3"><div className="text-[10px] text-[#8FA3BD]">KYC</div><div className="font-bold">{search.detail.user.kyc_status}</div></div>
              </div>
              <div className="bento-card p-4 mt-3">
                <h4 className="font-semibold mb-2">💰 العمولات المستلمة من هذا المستخدم</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-[#8FA3BD]">إيداع:</span><span className="text-[#00E676]">${search.detail.commissions.deposit.total.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-[#8FA3BD]">جمعية:</span><span className="text-[#00E676]">${search.detail.commissions.pool.total.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-[#8FA3BD]">تروي:</span><span className="text-[#00E676]">${search.detail.commissions.promo.total.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-[#8FA3BD]">إعلانات:</span><span className="text-[#00E676]">${search.detail.commissions.ad_view.total.toFixed(2)}</span></div>
                  <div className="flex justify-between pt-2 mt-2 border-t border-[#00E676]/40">
                    <span className="font-bold">الإجمالي:</span>
                    <span className="text-cyan-gradient font-bold text-lg" data-testid="user-total-commission">${search.detail.total_commissions.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
